import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colorTokens } from '../../app/designTokens';
import {
  filterCountriesForSearch,
  formatCountryOptionLabel,
  formatPhonePrefixLabel,
} from '../../utils/countryDisplay';
import { RegisterSectionCard } from './RegisterSectionCard';

const { semantic } = colorTokens;

/**
 * Customer-data fields shared by public /register and admin /onboarding/trial.
 * Field order, labels, country options and conditional business/consumer
 * visibility stay in this one component.
 */
export function RegisterCustomerFields({
  form,
  stepErrors,
  optionsWarning,
  registerOptions,
  selectedCountry,
  phonePrefixCountries,
  phonePrefixCountryCode,
  phoneValidationError,
  consentAccepted,
  onFieldChange,
  onCompanyNameChange,
  onCustomerTypeChange,
  onCountryChange,
  onPhonePrefixChange,
  onPhoneNumberChange,
  onConsentChange,
  findCountryByCode,
}) {
  const { t } = useTranslation(['onboarding', 'auth', 'register', 'common']);
  const isBusiness = form.customer_type === 'business';
  const isConsumer = form.customer_type === 'consumer';

  return (
    <Stack spacing={2} sx={{ flex: 1, minWidth: 0, width: '100%' }} data-testid="register-customer-fields">
      {optionsWarning ? (
        <Alert severity="warning">{t('register:optionsUnavailable')}</Alert>
      ) : null}

      <RegisterSectionCard
        title={t('onboarding:register.wizard.company_contact_heading')}
        helper={t('onboarding:register.wizard.company_contact_helper')}
        testId="register-company-contact"
      >
        <Stack spacing={2}>
          <FormControl error={Boolean(stepErrors.customer_type)} required data-testid="register-customer-type">
            <FormLabel id="register-customer-type-label">
              {t('onboarding:register.wizard.customer_type_label')}
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="register-customer-type-label"
              name="customer_type"
              value={form.customer_type || ''}
              onChange={onCustomerTypeChange}
            >
              <FormControlLabel
                value="business"
                control={<Radio inputProps={{ 'data-testid': 'register-customer-type-business' }} />}
                label={t('onboarding:register.wizard.customer_type_business')}
              />
              <FormControlLabel
                value="consumer"
                control={<Radio inputProps={{ 'data-testid': 'register-customer-type-consumer' }} />}
                label={t('onboarding:register.wizard.customer_type_consumer')}
              />
            </RadioGroup>
            <FormHelperText data-testid="register-customer-type-helper">
              {stepErrors.customer_type
                ? t('onboarding:register.wizard.customer_type_required')
                : t('onboarding:register.wizard.customer_type_helper')}
            </FormHelperText>
          </FormControl>
          <TextField
            label={t('auth:company_name')}
            required={isBusiness}
            value={form.company_name}
            onChange={onCompanyNameChange}
            error={Boolean(stepErrors.company_name)}
            helperText={isConsumer ? t('onboarding:register.wizard.company_name_optional_consumer') : undefined}
            inputProps={{ 'data-testid': 'register-company-name' }}
          />
          <TextField
            label={t('auth:contact_name')}
            required
            value={form.contact_name}
            onChange={onFieldChange('contact_name')}
            error={Boolean(stepErrors.contact_name)}
            inputProps={{ 'data-testid': 'register-contact-name' }}
          />
          <TextField
            label={t('auth:contact_email')}
            required
            type="email"
            value={form.contact_email}
            onChange={onFieldChange('contact_email')}
            error={Boolean(stepErrors.contact_email)}
            inputProps={{ 'data-testid': 'register-contact-email' }}
          />

          <Autocomplete
            id="register-country-autocomplete"
            options={registerOptions.countries || []}
            value={selectedCountry}
            onChange={onCountryChange}
            getOptionLabel={(option) => formatCountryOptionLabel(option)}
            filterOptions={(options, { inputValue }) => filterCountriesForSearch(options, inputValue)}
            isOptionEqualToValue={(option, value) => option?.code === value?.code}
            disableClearable
            renderOption={(props, option) => (
              <li {...props} key={option.code}>
                {formatCountryOptionLabel(option)}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('register:country')}
                placeholder={t('register:searchCountry')}
                helperText={optionsWarning ? t('register:countryDefaulted') : t('register:selectCountry')}
                inputProps={{
                  ...params.inputProps,
                  'data-testid': 'register-country',
                }}
              />
            )}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl
              sx={{ minWidth: 180 }}
              error={Boolean(phoneValidationError || stepErrors.phone_national_number)}
            >
              <InputLabel id="phone-country-code-label">{t('register:phoneCountryCode')}</InputLabel>
              <Select
                labelId="phone-country-code-label"
                label={t('register:phoneCountryCode')}
                value={phonePrefixCountryCode}
                onChange={onPhonePrefixChange}
                inputProps={{ 'data-testid': 'register-phone-country-code' }}
                renderValue={(code) => {
                  const country = findCountryByCode(phonePrefixCountries, code);
                  return country ? formatPhonePrefixLabel(country) : form.phone_country_code;
                }}
              >
                {phonePrefixCountries.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {formatPhonePrefixLabel(country)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('register:phoneNumber')}
              value={form.phone_national_number}
              onChange={onPhoneNumberChange}
              placeholder="901 123 456"
              helperText={phoneValidationError || t('register:phoneHelp')}
              error={Boolean(phoneValidationError || stepErrors.phone_national_number)}
              fullWidth
              inputProps={{ 'data-testid': 'register-phone-number' }}
            />
          </Stack>
          <Typography sx={{ fontSize: 12, color: semantic.text.muted }}>
            {t('register:phoneOptional')}
          </Typography>
        </Stack>
      </RegisterSectionCard>

      {isBusiness ? (
        <RegisterSectionCard
          title={t('onboarding:register.wizard.company_identifiers_heading')}
          helper={t('onboarding:register.wizard.company_identifiers_helper')}
          testId="register-company-identifiers"
        >
          <Stack spacing={2}>
            <TextField
              label={t('onboarding:register.wizard.company_id_number')}
              value={form.company_id_number}
              onChange={onFieldChange('company_id_number')}
              inputProps={{ 'data-testid': 'register-company-id-number' }}
            />
            <TextField
              label={t('onboarding:register.wizard.tax_id')}
              value={form.tax_id}
              onChange={onFieldChange('tax_id')}
              inputProps={{ 'data-testid': 'register-tax-id' }}
            />
            <TextField
              label={t('onboarding:register.wizard.vat_id')}
              value={form.vat_id}
              onChange={onFieldChange('vat_id')}
              inputProps={{ 'data-testid': 'register-vat-id' }}
            />
          </Stack>
        </RegisterSectionCard>
      ) : null}

      <RegisterSectionCard
        title={t('onboarding:register.wizard.company_address_heading')}
        testId="register-company-address"
      >
        <Stack spacing={2}>
          <TextField
            label={t('onboarding:register.wizard.address_line1')}
            value={form.address_line1}
            onChange={onFieldChange('address_line1')}
            inputProps={{ 'data-testid': 'register-address-line1' }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('onboarding:register.wizard.city')}
              value={form.city}
              onChange={onFieldChange('city')}
              fullWidth
              inputProps={{ 'data-testid': 'register-city' }}
            />
            <TextField
              label={t('onboarding:register.wizard.postal_code')}
              value={form.postal_code}
              onChange={onFieldChange('postal_code')}
              fullWidth
              inputProps={{ 'data-testid': 'register-postal-code' }}
            />
          </Stack>
        </Stack>
      </RegisterSectionCard>

      <RegisterSectionCard
        title={t('onboarding:register.wizard.notes_heading')}
        helper={t('onboarding:register.wizard.notes_helper')}
        testId="register-company-notes"
      >
        <Stack spacing={1.5}>
          <TextField
            label={t('auth:notes')}
            multiline
            minRows={2}
            value={form.notes}
            onChange={onFieldChange('notes')}
            inputProps={{ 'data-testid': 'register-notes' }}
          />
          <Box>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={consentAccepted}
                  onChange={onConsentChange}
                  inputProps={{ 'data-testid': 'register-consent' }}
                />
              )}
              label={t('onboarding:register.wizard.consent_label')}
            />
            {stepErrors.consent ? (
              <Typography sx={{ fontSize: 12, color: semantic.error, display: 'block' }}>
                {t('onboarding:register.wizard.consent_required')}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </RegisterSectionCard>
    </Stack>
  );
}

export default RegisterCustomerFields;
